import React from "react";
import { motion } from "framer-motion";
import {
  GraduationCap,
  UserCheck,
  Award,
  Users,
  Brain,
  Presentation,
} from "lucide-react";

const KeyHighlights: React.FC = () => {
  const highlights = [
    {
      icon: GraduationCap,
      title: "Direct Academic Oversight by Cambridge Professors",
      keyMessage:
        "This curriculum is designed, supervised, and assessed by University of Cambridge Professors.",
      detail:
        "Unlike standard summer camps, the syllabus and assessment criteria are developed within the Professor's own academic framework, ensuring authentic university-level rigour.",
      gradient: "from-indigo-500 to-blue-600",
    },
    {
      icon: UserCheck,
      title: "High-Calibre On-Site Mentorship",
      keyMessage:
        "Students receive daily, in-person instruction from Cambridge PhD female researchers.",
      detail:
        "While Professors lead lectures online (and selected in-person sessions), these doctoral experts are on-site full-time to guide workshops, supervise projects, and provide personal mentorship.",
      gradient: "from-blue-500 to-purple-600",
    },
    {
      icon: Award,
      title: "A Competitive Edge: The Recommendation Letter",
      keyMessage:
        "The top 50% of students in each programme will earn a Letter of Recommendation signed by the supervising Cambridge Professor.",
      detail:
        "This is a merit-based award judged on academic performance, engagement, and the quality of the final presentation, offering proof of excellence to future admissions officers.",
      gradient: "from-purple-500 to-pink-600",
    },
    {
      icon: Users,
      title: "Bespoke, Small-Group Learning",
      keyMessage:
        "Classes are strictly limited to a maximum of 17 students per subject stream.",
      detail:
        "This low student-to-staff ratio ensures that every participant receives personalised feedback on their research and presentation skills.",
      gradient: "from-pink-500 to-red-600",
    },
    {
      icon: Brain,
      title: "Career-Defining Subject Streams",
      keyMessage:
        "Students can choose from three specialised streams: Entrepreneurship, Artificial Intelligence (AI), or Medicine.",
      detail:
        "These subjects are designed to bridge the gap between theoretical school knowledge and real-world professional challenges.",
      gradient: "from-red-500 to-orange-600",
    },
    {
      icon: Presentation,
      title: "Tangible Research Output & Skill Development",
      keyMessage:
        "The programme culminates in a Final Project Presentation assessed by a Cambridge Professor.",
      detail:
        "By Day 12, students will have formulated a research question, analysed evidence, and defended their findings in a formal academic setting, mirroring the university undergraduate experience.",
      gradient: "from-orange-500 to-yellow-600",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Why This Programme Stands Out
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Discover what makes our Cambridge Research Programmes a
            transformative educational experience
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {highlights.map((highlight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
            >
              <div
                className={`w-14 h-14 bg-gradient-to-br ${highlight.gradient} rounded-xl flex items-center justify-center mb-6 shadow-md`}
              >
                <highlight.icon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                {highlight.title}
              </h3>
              <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-l-4 border-blue-500">
                <p className="text-sm font-semibold text-gray-800 leading-relaxed">
                  {highlight.keyMessage}
                </p>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                {highlight.detail}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default KeyHighlights;
